if (!customElements.get('svelli-hero-slider')) {
  customElements.define('svelli-hero-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.setAttribute('data-popup-pending', '');
      this.popupTimer = setTimeout(() => this.removeAttribute('data-popup-pending'), 25000);
      const signal = this.controller.signal;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.videos = this.slides.map(slide => slide.querySelector('video'));
      this.index = 0;
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay !== 'false';
      this.userPaused = !this.autoplay || this.reducedMotion.matches;
      this.pauseButton = this.querySelector('[data-pause]');
      const on = (el, event, fn, options = {}) => el?.addEventListener(event, fn, { ...options, signal });
      const refresh = () => this.refresh();
      this.videos.forEach((video, index) => {
        video.muted = true;
        video.defaultMuted = true;
        video.volume = 0;
        video.autoplay = true;
        video.loop = true;
        video.playsInline = true;
        on(video, 'loadedmetadata', () => { if (index === this.index) this.refreshVideo(); });
        on(video, 'loadeddata', () => { if (index === this.index) this.refreshVideo(); });
        on(video, 'canplay', () => { if (index === this.index) this.refreshVideo(); });
        on(video, 'playing', () => this.slides[index].classList.remove('svh-svelli-video-blocked'));
        on(video, 'error', () => this.slides[index].classList.add('svh-svelli-video-blocked'));
      });
      this.dots.forEach((dot, index) => on(dot, 'click', () => this.show(index, true)));
      on(this.querySelector('[data-prev]'), 'click', () => this.show(this.index - 1, true));
      on(this.querySelector('[data-next]'), 'click', () => this.show(this.index + 1, true));
      on(this.pauseButton, 'click', () => { this.userPaused = !this.userPaused; this.refresh(); });
      on(this, 'mouseenter', () => { this.hovered = true; this.refresh(); });
      on(this, 'mouseleave', () => { this.hovered = false; this.refresh(); });
      on(this, 'focusin', () => { this.focused = true; this.refresh(); });
      on(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.refresh(); } });
      on(this, 'keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        }
      });
      let touchStart;
      on(this, 'touchstart', event => { const touch = event.touches[0]; touchStart = { x: touch.clientX, y: touch.clientY }; }, { passive: true });
      on(this, 'touchend', event => {
        if (!touchStart) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - touchStart.x;
        const dy = touch.clientY - touchStart.y;
        touchStart = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1), true);
      }, { passive: true });
      on(this, 'touchcancel', () => { touchStart = null; }, { passive: true });
      on(document, 'visibilitychange', refresh);
      on(window, 'pageshow', refresh);
      on(window, 'focus', refresh);
      on(this.reducedMotion, 'change', () => { if (this.reducedMotion.matches) this.userPaused = true; this.refresh(); });
      on(document, 'pointerdown', () => this.refreshVideo(), { passive: true });
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.refresh(); }, { threshold: .1 });
      this.observer.observe(this);
      this.refresh();
    }
    hydrateVideo(index) {
      const video = this.videos[index];
      const source = video.querySelector('source[data-src]');
      if (source) { source.src = source.dataset.src; source.removeAttribute('data-src'); video.load(); }
      return video;
    }
    show(index, manual = false) {
      index = (index + this.slides.length) % this.slides.length;
      const changed = index !== this.index;
      this.index = index;
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        this.dots[i].setAttribute('aria-pressed', String(active));
      });
      const video = this.hydrateVideo(index);
      if (changed && video.readyState) video.currentTime = 0;
      if (manual) this.userPaused = true;
      this.refresh();
    }
    refreshVideo() {
      this.videos.forEach((video, i) => {
        if (i !== this.index || !this.visible || document.hidden) { video.pause(); return; }
        if (!video.paused) return;
        video.muted = true;
        const playing = video.play();
        if (playing) playing.catch(() => this.slides[i].classList.add('svh-svelli-video-blocked'));
      });
    }
    refresh() {
      clearTimeout(this.timer);
      if (this.pauseButton) {
      this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
      this.pauseButton.setAttribute('aria-label', this.userPaused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
      this.pauseButton.innerHTML = this.userPaused
        ? '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5 3l8 5-8 5z"/></svg>'
        : '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5 3v10M11 3v10"/></svg>';
      }
      this.refreshVideo();
      if (!this.userPaused && !this.hovered && !this.focused && this.visible && !document.hidden && !this.reducedMotion.matches) {
        this.timer = setTimeout(() => this.show(this.index + 1), Number(this.slides[this.index].dataset.delay || this.dataset.delay) || 8000);
      }
    }
    disconnectedCallback() {
      clearTimeout(this.popupTimer);
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.videos?.forEach(video => video.pause());
      this.controller = null;
    }
  });
}
